/**
 * The `['auth','me']` query — the single owner of the authenticated-user server
 * state. Mounted once (App) so it self-refreshes on `staleTime`/window-focus,
 * which is the fix for the stale-user bug (roles/entitlements/assignableOwners
 * previously only refreshed on an imperative setUser).
 */
import { useQuery } from '@tanstack/react-query';
import { authMeQueryOptions } from '@/api/auth';
import { queryClient } from '@/api/queryClient';
import { readSeed } from '@/auth/authData';
import { hasSession, isTokenExchangeArrival } from '@/auth/session';
import type { User } from '@/types';

/** How long a fetched user is considered fresh before a focus/mount refetch. */
export const AUTH_STALE_TIME = 1000 * 60 * 5; // 5 minutes

/**
 * May the mounted auth query act on the stored session right now?
 *
 * `hasSession()` alone is not enough on a `/sso?auth_token=…` arrival: the
 * stored token is the one the person's ENDED session left behind, and firing
 * `getMe()` with it 401s → the client bounces to
 * `/login/signIn?reason=session_rejected` → the page unloads before `Sso`
 * exchanges the fresh token. The arrival owns the session; this query waits.
 */
function storedSessionIsActionable(): boolean {
  return hasSession() && !isTokenExchangeArrival();
}

export function useAuthQuery() {
  // Pin to the module queryClient (the same one the imperative auth accessors in
  // authData read/write) rather than the provider's client. In production these
  // are identical (main.tsx provides this client); in tests renderWithProviders
  // supplies its own client, so without this pin a `signIn()` write and the
  // component's read would land on different caches.
  return useQuery<User>({
    ...authMeQueryOptions(),
    enabled: storedSessionIsActionable(),
    staleTime: AUTH_STALE_TIME,
    refetchOnWindowFocus: true,
    // Paint the last-known user instantly on reload, but mark it already-stale
    // so a real refetch runs on mount (main.tsx primes the cache the same way).
    //
    // Gated on `hasSession()` for the same reason main.tsx gates its priming and
    // the auth store gates its own seed (vingreact#82): the seed is a PAINT
    // optimization, never evidence of a session. Ungated, a signed-OUT visitor
    // whose seed outlived their token got that user written into the cache at
    // query creation — even though `enabled` is false and nothing would ever
    // fetch to correct it. The cache→store mirror then flipped the auth store to
    // `isAuthenticated: true`, ProtectedRoute admitted them to /dashboard, and
    // the app ran tokenless on a user object the next auth failure nulls out.
    initialData: () => (storedSessionIsActionable() ? (readSeed() ?? undefined) : undefined),
    initialDataUpdatedAt: 0,
  }, queryClient);
}
